'use client';

import clsx from 'clsx';
import { ReactNode, useLayoutEffect, useRef } from 'react';

import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

export interface ScrollAnimatedTextLine {
	text: ReactNode;
	className?: string;
	innerClassName?: string;
}

interface ScrollAnimatedTextProps {
	lines: ScrollAnimatedTextLine[];
	className?: string;
	children?: ReactNode;
}

const ScrollAnimatedText = ({ lines, className = '', children }: ScrollAnimatedTextProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const textRef = useRef<HTMLHeadingElement>(null);

	useLayoutEffect(() => {
		const wrapper = wrapperRef.current;
		const textEl = textRef.current;

		if (!wrapper || !textEl) return;

		const lineInners = Array.from(textEl.querySelectorAll(`.${css.lineInner}`)) as HTMLElement[];
		const linesCount = lineInners.length;

		const matchMedia = gsap.matchMedia();

		matchMedia.add(
			{
				isDesktop: `(min-width: 992px)`,
				isMobile: `(max-width: 991px)`
			},
			() => {
				const perLineRange = 1 / Math.max(3, linesCount);
				const overlap = 0.2;
				const effectiveRange = perLineRange + overlap * perLineRange;

				gsap.timeline({
					scrollTrigger: {
						trigger: wrapper,
						start: 'top bottom',
						end: 'bottom+=100% top',
						scrub: true,
						onUpdate: ({ progress }) => {
							const normalized = gsap.utils.clamp(0, 1, progress / 0.45);

							lineInners.forEach((el, i) => {
								const center = i / Math.max(1, linesCount - 1);
								const start = Math.max(0, center - effectiveRange / 2);
								const end = Math.min(1, center + effectiveRange / 2);

								let localProg = 0;
								if (normalized <= start) localProg = 0;
								else if (normalized >= end) localProg = 1;
								else localProg = (normalized - start) / (end - start);

								const hiddenPercent = Math.round((1 - localProg) * 10000) / 100;
								gsap.set(el, { clipPath: `inset(0 ${hiddenPercent}% 0 0)` });
							});
						}
					}
				});
			},
			wrapper
		);

		return () => {
			matchMedia.revert();
		};
	}, []);

	return (
		<div ref={wrapperRef} className={css.wrapper}>
			<h2 ref={textRef} className={clsx(css.animatedText, className)}>
				{lines.map((line, index) => (
					<div key={index} className={clsx(css.line, line.className)}>
						<span className={clsx(css.lineInner, line.innerClassName)}>{line.text}</span>
					</div>
				))}
			</h2>
			{children}
		</div>
	);
};

export default ScrollAnimatedText;
